import { useState, type FormEvent } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import { categories, contactInfo } from "@/pages/home/data";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahs8g3oh653ivfvo4ng";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;

    // Read the real DOM values first (covers browser autofill too).
    const formData = new FormData(formEl);
    const decoy = String(formData.get("website_alt") ?? "").trim();
    if (decoy) {
      // Silent generic success so bots get no clear signal.
      setStatus("success");
      formEl.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = typeof value === "string" ? value : value.name;
      if (text.trim() === "") return;
      payload.append(key, text);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || isSpam || parsed?.code !== "OK") {
        setStatus("error");
        setFormError(serverMsg || "提交未成功，请稍后重试。");
        return;
      }

      setStatus("success");
      formEl.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未送达，请稍后重试。");
    }
  };

  return (
    <section id="contact" className="scroll-mt-20 w-full px-4 md:px-8 py-16 md:py-24 bg-background-100">
      <div className="max-w-6xl mx-auto">
        <SectionHeading
          eyebrow="联系我们"
          title="投稿前有疑问？先咨询再提交"
          description="不确定材料是否齐全、分类是否选对，或想了解某条标准的执行尺度，都可以先联系我们。带上初步材料或查询编号，我们能给出更具体的建议。"
        />

        <div className="mt-10 md:mt-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-3">
            {[
              {
                icon: "ri-mail-send-line",
                label: "审核咨询邮箱",
                value: contactInfo.email,
              },
              {
                icon: "ri-time-line",
                label: "回复时效",
                value: contactInfo.replyTime,
              },
              {
                icon: "ri-hashtag",
                label: "进度跟进",
                value: contactInfo.followUp,
              },
              {
                icon: "ri-shield-check-line",
                label: "隐私说明",
                value: "投稿人身份信息默认不公开，材料仅用于审核与核验。",
              },
            ].map((row) => (
              <div
                key={row.label}
                className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200"
              >
                <span className="shrink-0 w-11 h-11 flex items-center justify-center rounded-md bg-primary-50 text-primary-600 text-xl ring-1 ring-primary-100">
                  <i className={row.icon} aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">{row.label}</p>
                  <p className="mt-1 text-sm font-medium text-foreground-900 break-all">
                    {row.value}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-3">
            <form
              id="tougao-inquiry-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="p-5 md:p-7 rounded-lg bg-background-50 border border-background-200"
            >
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                投稿咨询
              </h3>
              <p className="mt-1.5 text-sm text-foreground-500">
                带 * 为必填项，我们会按你留下的邮箱回复。
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800">
                    称呼 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="如何称呼你"
                    className="mt-2 w-full px-3.5 py-2.5 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800">
                    邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="用于接收回复"
                    className="mt-2 w-full px-3.5 py-2.5 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="category" className="block text-sm font-medium text-foreground-800">
                  拟投稿分类
                </label>
                <select
                  id="category"
                  name="category"
                  defaultValue=""
                  className="mt-2 w-full px-3.5 py-2.5 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 cursor-pointer"
                >
                  <option value="">请选择分类（可暂不确定）</option>
                  {categories.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                  <option value="审核标准咨询">审核标准咨询</option>
                  <option value="删除原因咨询">删除原因咨询</option>
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-800">
                  咨询内容 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="简单描述你的事件类型、已有材料，以及想了解的标准或流程问题（不超过 500 字）"
                  className="mt-2 w-full px-3.5 py-2.5 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
                ></textarea>
                <p className="mt-1.5 text-xs text-foreground-400">
                  请勿在咨询中填写与事件无关的他人身份信息。
                </p>
              </div>

              <input
                type="text"
                name="website_alt"
                className="field-extra"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i
                    className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"}
                    aria-hidden="true"
                  ></i>
                  {status === "submitting" ? "提交中..." : "提交咨询"}
                </button>

                {status === "success" && (
                  <p className="flex items-center gap-2 text-sm text-secondary-700">
                    <i className="ri-checkbox-circle-fill" aria-hidden="true"></i>
                    已收到你的咨询，我们会按邮箱回复。
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-start gap-2 text-sm text-primary-700">
                    <i className="ri-error-warning-fill mt-0.5" aria-hidden="true"></i>
                    {formError || "提交未成功，请稍后重试。"}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}